import { useTranslation } from 'react-i18next';
import { Box, Divider, Menu, Typography } from '@mui/material';
import { type GradeRecord } from 'views/DispatchImporters/GradeImporterView';

export interface GradeDetailsState {
  mouseX: number;
  mouseY: number;
  grade: GradeRecord;
}

interface GradeDetailsPopoverProps {
  target: GradeDetailsState | null;
  onClose: () => void;
}

export function GradeDetailsPopover({
  target,
  onClose,
}: GradeDetailsPopoverProps): JSX.Element {
  const { t } = useTranslation();
  const grade = target?.grade;

  const rows: Array<{ label: string; value: string }> = grade
    ? [
        {
          label: t('grade_importer_col_material'),
          value: grade.materialCategory ?? '—',
        },
        {
          label: t('grade_importer_col_category'),
          value: grade.gradeCategory ?? '—',
        },
        {
          label: t('grade_importer_col_specification'),
          value: grade.specification,
        },
        {
          label: t('grade_importer_col_inventory'),
          value: grade.inventory != null ? String(grade.inventory) : '—',
        },
        {
          label: t('grade_importer_col_specific_gravity'),
          value:
            grade.specificGravity != null ? String(grade.specificGravity) : '—',
        },
      ]
    : [];

  const qualityRows = Object.entries(grade?.qualities ?? {});

  return (
    <Menu
      open={target !== null}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={
        target ? { top: target.mouseY, left: target.mouseX } : undefined
      }
    >
      {grade && (
        <Box sx={{ px: 2, py: 1, minWidth: 220 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {grade.name}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {rows.map((row) => (
            <Box
              key={row.label}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                py: 0.25,
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {row.label}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {row.value}
              </Typography>
            </Box>
          ))}
          {qualityRows.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontWeight: 600 }}
              >
                {t('grade_importer_col_qualities')}
              </Typography>
              {qualityRows.map(([key, value]) => (
                <Box
                  key={key}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                    py: 0.25,
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {key}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </Box>
      )}
    </Menu>
  );
}
